import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FeatureFlagsService } from '@principle-theorem/ng-feature-flags';
import {
  AccountingFunctionsService,
  CurrentScopeFacade,
  StateBasedNavigationService,
} from '@principle-theorem/ng-principle-shared';
import { BasicDialogService } from '@principle-theorem/ng-shared';
import {
  Invoice,
  Practice,
  RefundHelpers,
  Transaction,
  Verifone,
} from '@principle-theorem/principle-core';
import { VERIFONE_FEATURE } from '@principle-theorem/principle-core/features';
import {
  IVerifonePurchaseTransactionExtendedData,
  TransactionAction,
  TransactionProvider,
  VerifoneTransactionResult,
  isVerifoneTransactionResponse,
  type IInvoice,
  type IPractice,
  type ITransaction,
  type IVerifonePurchaseRequest,
  type IVerifoneRefundRequest,
  type IVerifoneTransactionResponse,
} from '@principle-theorem/principle-core/interfaces';
import {
  Region,
  filterUndefined,
  snapshot,
  type DocumentReference,
  type WithRef,
} from '@principle-theorem/shared';
import { of, type Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { RefundDepositPromptService } from '../transaction-components/refund-deposit-prompt/refund-deposit-prompt.service';
import { TransactionAmountDialog } from '../transaction-components/transaction-amount-dialog/transaction-amount-dialog.service';
import { refundAvailableInfo } from '../transaction-helpers';
import {
  TransactionProviderType,
  type ITransactionProvider,
} from '../transaction-provider';
import { SelectVerifoneTerminalService } from './select-verifone-terminal/select-verifone-terminal.service';
import { VerifonePurchaseBuilder } from './verifone-purchase-builder.service';
import { VerifoneTransactionDialogService } from './verifone-transaction-dialog/verifone-transaction-dialog.service';

@Injectable()
export class VerifoneCardPurchaseTransactionProvider
  implements ITransactionProvider
{
  providerId = TransactionProvider.VerifoneCard;
  providerType = TransactionProviderType.Payment;
  providerRegions = [Region.NewZealand];

  isEnabled$: Observable<boolean>;

  constructor(
    private _transactionDialog: VerifoneTransactionDialogService,
    private _currentScopeFacade: CurrentScopeFacade,
    private _selectTerminal: SelectVerifoneTerminalService,
    private _purchaseBuilder: VerifonePurchaseBuilder,
    private _basicDialog: BasicDialogService,
    private _refundDepositPrompt: RefundDepositPromptService,
    private _accountFunctions: AccountingFunctionsService,
    private _featureFlags: FeatureFlagsService,
    private _snackbar: MatSnackBar,
    private _amountDialog: TransactionAmountDialog,
    private _stateNav: StateBasedNavigationService
  ) {
    this.isEnabled$ = this._currentScopeFacade.currentPractice$.pipe(
      switchMap((practice) =>
        this._featureFlags
          .isFeatureEnabled$(VERIFONE_FEATURE)
          .pipe(
            map(
              (isEnabled) => isEnabled && Practice.isVerifoneEnabled(practice)
            )
          )
      )
    );
  }

  canCapture$(invoice: WithRef<IInvoice>): Observable<boolean> {
    const canCapture =
      !Invoice.isPaid(invoice) && Invoice.canAddTransactions(invoice);
    return of(canCapture);
  }

  async capture(
    invoice: WithRef<IInvoice>
  ): Promise<DocumentReference<ITransaction> | undefined> {
    const formData = await this._purchaseBuilder.openPurchaseForm(invoice);
    if (!formData || formData.amount <= 0) {
      return;
    }

    const terminal = await this._selectTerminal.getTerminalData();
    if (!terminal) {
      return;
    }

    const practice = await snapshot(
      this._currentScopeFacade.currentPractice$.pipe(filterUndefined())
    );

    const request = this._purchaseBuilder.buildPurchaseRequest(
      invoice,
      formData,
      terminal.terminalData,
      practice.verifoneSettings
    );

    try {
      const response = await this._transactionDialog.executePurchase(request);

      if (!response) {
        return;
      }

      if (!isVerifoneTransactionResponse(response)) {
        // eslint-disable-next-line no-console
        console.error('Invalid data for purchase response', response);
        await this._basicDialog.alert({
          title: 'Transaction Error',
          prompt:
            'Encountered an unknown error during transaction. Please try again.',
          submitLabel: 'Close',
        });
        return;
      }

      if (
        response.result === VerifoneTransactionResult.Unknown ||
        response.result === VerifoneTransactionResult.Pending
      ) {
        return await this._savePendingPurchase(
          invoice,
          terminal.practiceRef,
          request,
          response
        );
      }

      if (!Verifone.isSuccessResult(response.result)) {
        const errorMessage = this._getErrorMessage(response.result);
        await this._basicDialog.alert({
          title: 'Transaction Failed',
          prompt: errorMessage,
          submitLabel: 'Close',
        });
        return;
      }

      const transaction = await Verifone.toPurchaseTransaction(
        invoice,
        terminal.practiceRef,
        request,
        response
      );

      return await this._accountFunctions.addTransactionToInvoice(
        invoice,
        transaction,
        TransactionAction.Add
      );
    } catch {
      return this._savePendingPurchase(invoice, terminal.practiceRef, request);
    }
  }

  canRefund$(invoice: WithRef<IInvoice>): Observable<boolean> {
    return RefundHelpers.isRefundable$(invoice);
  }

  refundInfo$(invoice: WithRef<IInvoice>): Observable<string[]> {
    return Invoice.transactions$(invoice).pipe(
      map((transactions) =>
        RefundHelpers.getTotalRefundRemaining(transactions)
      ),
      map((refundRemaining) => [refundAvailableInfo(refundRemaining)])
    );
  }

  async refundTransaction(
    invoice: WithRef<IInvoice>,
    transaction: WithRef<ITransaction<IVerifonePurchaseTransactionExtendedData>>
  ): Promise<DocumentReference<ITransaction> | undefined> {
    const acceptedRefundAlert =
      await this._refundDepositPrompt.showRefundDepositAlert(invoice);
    if (!acceptedRefundAlert) {
      return;
    }
    const formData = await this._purchaseBuilder.openRefundForm({
      invoice,
      transaction,
      latestTransaction: transaction,
    });
    if (!formData || formData.amount <= 0) {
      return;
    }
    const terminal = await this._selectTerminal.getTerminalData();
    if (!terminal) {
      return;
    }

    const practice = await snapshot(
      this._currentScopeFacade.currentPractice$.pipe(filterUndefined())
    );

    const request = this._purchaseBuilder.buildRefundRequest(
      invoice,
      formData,
      terminal.terminalData,
      practice.verifoneSettings
    );

    try {
      const response = await this._transactionDialog.executeRefund(request);

      if (!response) {
        return;
      }

      if (!isVerifoneTransactionResponse(response)) {
        // eslint-disable-next-line no-console
        console.error('Invalid data for refund response', response);
        await this._basicDialog.alert({
          title: 'Transaction Error',
          prompt:
            'Encountered an unknown error during transaction. Please try again.',
          submitLabel: 'Close',
        });
        return;
      }

      if (
        response.result === VerifoneTransactionResult.Unknown ||
        response.result === VerifoneTransactionResult.Pending
      ) {
        return await this._savePendingRefund(
          invoice,
          terminal.practiceRef,
          request,
          response
        );
      }

      if (!Verifone.isSuccessResult(response.result)) {
        const errorMessage = this._getErrorMessage(response.result);
        await this._basicDialog.alert({
          title: 'Refund Failed',
          prompt: errorMessage,
          submitLabel: 'Close',
        });
        return;
      }

      const refundTransaction = await Verifone.toRefundTransaction(
        invoice,
        terminal.practiceRef,
        request,
        response,
        transaction
      );
      return await this._accountFunctions.addTransactionToInvoice(
        invoice,
        refundTransaction,
        TransactionAction.Refund
      );
    } catch {
      return this._savePendingRefund(invoice, terminal.practiceRef, request);
    }
  }

  async refund(
    invoice: WithRef<IInvoice>,
    transaction?: WithRef<ITransaction>
  ): Promise<DocumentReference<ITransaction> | undefined> {
    const acceptedRefundAlert =
      await this._refundDepositPrompt.showRefundDepositAlert(invoice);
    if (!acceptedRefundAlert) {
      this._snackbar.open('Transaction Cancelled');
      return;
    }
    const formData = await this._amountDialog.openRefund(
      'Verifone - EFTPOS Refund',
      invoice,
      transaction
    );
    if (!formData || formData.amount <= 0) {
      this._snackbar.open('Transaction Cancelled');
      return;
    }

    const terminal = await this._selectTerminal.getTerminalData();
    if (!terminal) {
      this._snackbar.open('Transaction Cancelled');
      return;
    }

    const practice = await snapshot(
      this._currentScopeFacade.currentPractice$.pipe(filterUndefined())
    );

    const request = this._purchaseBuilder.buildRefundRequest(
      invoice,
      formData,
      terminal.terminalData,
      practice.verifoneSettings
    );

    try {
      const response = await this._transactionDialog.executeRefund(request);

      if (!response) {
        return;
      }

      if (!isVerifoneTransactionResponse(response)) {
        // eslint-disable-next-line no-console
        console.error('Invalid data for refund response', response);
        await this._basicDialog.alert({
          title: 'Transaction Error',
          prompt:
            'Encountered an unknown error during transaction. Please try again.',
          submitLabel: 'Close',
        });
        return;
      }

      if (
        response.result === VerifoneTransactionResult.Unknown ||
        response.result === VerifoneTransactionResult.Pending
      ) {
        return await this._savePendingRefund(
          invoice,
          terminal.practiceRef,
          request,
          response
        );
      }

      if (!Verifone.isSuccessResult(response.result)) {
        const errorMessage = this._getErrorMessage(response.result);
        await this._basicDialog.alert({
          title: 'Refund Failed',
          prompt: errorMessage,
          submitLabel: 'Close',
        });
        return;
      }

      const refundTransaction = await Verifone.toRefundTransaction(
        invoice,
        terminal.practiceRef,
        request,
        response,
        transaction as WithRef<
          ITransaction<IVerifonePurchaseTransactionExtendedData>
        >
      );
      return await this._accountFunctions.addTransactionToInvoice(
        invoice,
        refundTransaction,
        TransactionAction.Refund
      );
    } catch {
      return this._savePendingRefund(invoice, terminal.practiceRef, request);
    }
  }

  async printReceipt(transaction: WithRef<ITransaction>): Promise<void> {
    const invoiceRef = Transaction.invoiceDocRef(transaction);
    const patientRef = Invoice.patientRef({ ref: invoiceRef });
    await this._stateNav.brand([
      'patients',
      patientRef.id,
      'account',
      'invoices',
      invoiceRef.id,
      'print-verifone-receipt',
      transaction.ref.id,
    ]);
  }

  private async _savePendingPurchase(
    invoice: WithRef<IInvoice>,
    practiceRef: DocumentReference<IPractice>,
    request: IVerifonePurchaseRequest,
    response?: IVerifoneTransactionResponse
  ): Promise<DocumentReference<ITransaction> | undefined> {
    const transaction = await Verifone.toPendingPurchaseTransaction(
      invoice,
      practiceRef,
      request,
      response
    );
    const transactionRef = await this._accountFunctions.addTransactionToInvoice(
      invoice,
      transaction,
      TransactionAction.Add
    );
    await this._showPendingVerificationAlert();
    return transactionRef;
  }

  private async _savePendingRefund(
    invoice: WithRef<IInvoice>,
    practiceRef: DocumentReference<IPractice>,
    request: IVerifoneRefundRequest,
    response?: IVerifoneTransactionResponse
  ): Promise<DocumentReference<ITransaction> | undefined> {
    const transaction = await Verifone.toPendingRefundTransaction(
      invoice,
      practiceRef,
      request,
      response
    );
    const transactionRef = await this._accountFunctions.addTransactionToInvoice(
      invoice,
      transaction,
      TransactionAction.Refund
    );
    await this._showPendingVerificationAlert();
    return transactionRef;
  }

  private async _showPendingVerificationAlert(): Promise<void> {
    await this._basicDialog.alert({
      title: 'Transaction Result Unknown',
      prompt:
        'The transaction result could not be confirmed. Please check the terminal to verify the outcome and then use the Approve or Void action on the transaction.',
      submitLabel: 'Close',
    });
  }

  private _getErrorMessage(result: VerifoneTransactionResult): string {
    switch (result) {
      case VerifoneTransactionResult.Failure:
        return 'The transaction was declined. Please try again or use an alternative payment method.';
      case VerifoneTransactionResult.Cancelled:
        return 'The transaction was cancelled.';
      case VerifoneTransactionResult.NoConnection:
        return 'Unable to connect to the terminal. Please check the terminal is powered on and connected.';
      case VerifoneTransactionResult.Unknown:
        return 'The transaction status is unknown. Please check the terminal for the result.';
      case VerifoneTransactionResult.Pending:
        return 'The transaction is still pending. Please wait for it to complete.';
      default:
        return 'An unexpected error occurred. Please try again.';
    }
  }
}
